/**
 * Display-only: map permission catalog rows to the same module / menu hierarchy as the production sidebar
 * (`FRONTEND_MENU_STRUCTURE`). Does not alter permission codes or API payloads.
 */

import {FRONTEND_MENU_STRUCTURE} from '../../../../_metronic/layout/components/sidebar/sidebar-menu/frontendMenuStructure'
import {normalizeBackendLabel} from '../../../../_metronic/layout/components/sidebar/sidebar-menu/backendMenuRouteMap'
import {
  isPermanentlyHiddenPermissionCode,
  isPermanentlyHiddenPlacement,
} from '../../../../app/shared/navigation/permanentlyHiddenMenus'
import {
  collapseGranularDbCategory,
  categorizePermissionFallback,
  humanizePermissionCode,
  resolveCatalogEntry,
  sortEntriesInModule,
  type PermissionCatalogEntry,
} from './permissionGrouping'
import {tryExplicitPlacement} from './productionPlacementExplicit'

type WalkNode = {
  label: string
  aliases?: string[]
  children?: WalkNode[]
}

/** Module order: sidebar order + Dashboard (catalog-only) + Other */
export const PRODUCTION_MODULE_ORDER = [
  ...FRONTEND_MENU_STRUCTURE.map((m) => m.label),
  'Dashboard',
  'Other',
] as const

export type ProductionLayoutPath = {
  moduleLabel: string
  /** Path under module, e.g. ['Reports','KPIs'] */
  segments: string[]
  /** Normalized match terms (aliases + words from labels) */
  terms: Set<string>
  sortIndex: number
  /** Synthetic row for parent-only matches (e.g. "Reports") */
  isSyntheticParent?: boolean
}

export type ProductionPermissionPlacement = {
  moduleLabel: string
  menuLabel: string
  subMenuLabel: string
  /** Full path under module for sorting / display */
  pathSegments: string[]
  sortIndex: number
  unmapped: boolean
  /** When unmapped or low confidence */
  otherSubgroup?: string | null
  /** How this row was placed (for diagnostics) */
  placementSource: 'explicit' | 'fuzzy' | 'unmapped'
}

const norm = (s: string) => String(s || '').trim().toUpperCase()

let _pathCache: ProductionLayoutPath[] | null = null
let _accountsPathCache: ProductionLayoutPath[] | null = null

function collectNodeTerms(label: string, aliases?: string[]): string[] {
  const out: string[] = []
  const add = (s: string) => {
    const t = normalizeBackendLabel(s)
    if (t.length < 2) return
    out.push(t)
    t.split(' ').forEach((w) => {
      if (w.length >= 3) out.push(w)
    })
  }
  add(label)
  ;(aliases || []).forEach(add)
  return out
}

function flattenMenuTree(
  moduleLabel: string,
  nodes: WalkNode[] | undefined,
  ancestorSegs: string[],
  pathAcc: ProductionLayoutPath[],
  sortRef: {n: number},
  includeSyntheticParents: boolean
): void {
  if (!nodes?.length) return
  for (const node of nodes) {
    const terms = new Set(collectNodeTerms(node.label, node.aliases))
    const segHere = [...ancestorSegs, node.label]

    if (node.children?.length) {
      if (includeSyntheticParents) {
        pathAcc.push({
          moduleLabel,
          segments: segHere,
          terms,
          sortIndex: sortRef.n++,
          isSyntheticParent: true,
        })
      }
      flattenMenuTree(moduleLabel, node.children, segHere, pathAcc, sortRef, includeSyntheticParents)
    } else {
      pathAcc.push({
        moduleLabel,
        segments: segHere,
        terms,
        sortIndex: sortRef.n++,
      })
    }
  }
}

/**
 * Locked production UI: **`Accounts`** is module/shell only in `FRONTEND_MENU_STRUCTURE` (no inferred ERP subtree).
 * Zoho/Books catalog rows map to **`Accounts`** top-level for grouping consistency with the sidebar shell.
 */
function flattenAccountsPaths(sortRef: {n: number}): ProductionLayoutPath[] {
  const terms = new Set<string>()
  collectNodeTerms('Accounts').forEach((t) => terms.add(t))
  ;['accounts', 'accounting', 'zoho', 'books'].forEach((alias) =>
    collectNodeTerms(alias).forEach((t) => terms.add(t))
  )
  return [
    {
      moduleLabel: 'Accounts',
      segments: ['Accounts'],
      terms,
      sortIndex: sortRef.n++,
    },
  ]
}

export function getProductionLayoutPaths(): ProductionLayoutPath[] {
  if (_pathCache) return _pathCache
  const paths: ProductionLayoutPath[] = []
  const sortRef = {n: 0}
  for (const mod of FRONTEND_MENU_STRUCTURE) {
    const ml = mod.label
    flattenMenuTree(ml, mod.children as WalkNode[] | undefined, [], paths, sortRef, true)
  }
  _pathCache = paths
  return _pathCache
}

function getAccountsLayoutPaths(): ProductionLayoutPath[] {
  if (_accountsPathCache) return _accountsPathCache
  _accountsPathCache = flattenAccountsPaths({n: 10000})
  return _accountsPathCache
}

/** Heuristic: integrate / books-style permissions */
export function catalogEntryLooksAccounts(entry: PermissionCatalogEntry): boolean {
  const u = norm(
    `${entry.permissionCode} ${entry.displayName || ''} ${entry.description || ''} ${entry.menuLabel || ''} ${entry.subMenuLabel || ''}`
  )
  if (!u) return false
  const keys = [
    'ZOH',
    'ZOHO',
    'BOOKS',
    'ACCOUNT',
    'ACCOUNTS',
    'INVOICE',
    'CUSTOMER',
    'VENDOR_CREDIT',
    'PAYMENT',
    'SALESORDER',
    'PURCHASEORDER',
    'QUOTE',
    'BILL',
    'CHALLAN',
    'GST',
    'SUBSCRIPTION',
  ]
  return keys.some((k) => u.includes(k))
}

export function catalogHasAnyAccountsPermission(catalog: PermissionCatalogEntry[]): boolean {
  return catalog.some(catalogEntryLooksAccounts)
}

export function entryCorpusNormalized(entry: PermissionCatalogEntry): string {
  const r = resolveCatalogEntry(entry)
  const raw = [
    r.permissionCode.replace(/_/g, ' '),
    r.displayName,
    r.description,
    r.menuLabel,
    r.subMenuLabel,
    r.moduleCategory,
  ]
    .filter(Boolean)
    .join(' ')
  return normalizeBackendLabel(raw)
}

function guessProductionModuleLabel(entry: PermissionCatalogEntry): string {
  const r = resolveCatalogEntry(entry)
  const code = norm(r.permissionCode)
  const catRaw = r.moduleCategory?.trim() || ''
  const collapsed =
    collapseGranularDbCategory(catRaw) || categorizePermissionFallback(code) || catRaw || 'Other'
  const collapsedNorm = normalizeBackendLabel(collapsed)

  if (collapsedNorm === 'dashboard' || /\bDASHBOARD\b|\bHOME_WIDGET\b/.test(code)) {
    return 'Dashboard'
  }

  if (
    catalogEntryLooksAccounts(entry) &&
    (collapsedNorm === 'accounts' ||
      collapsedNorm === 'admin' ||
      code.includes('ZOHO') ||
      code.includes('BOOKS'))
  ) {
    return 'Accounts'
  }

  if (
    collapsedNorm === 'procurement' ||
    collapsedNorm === 'inventory' ||
    (/\bPROCUREMENT\b|\bREQUISITION\b|\bRFQ\b|\bQUOTATION\b|\bGOODS_RECEIPT\b|\bGRN\b|\bPO_\b|PURCHASE_ORDER/.test(code) &&
      !collapsedNorm.includes('qhse'))
  ) {
    return 'Technical'
  }

  if (collapsedNorm === 'reports' && !/\bQHSE\b/.test(code)) {
    return 'Operations'
  }

  for (const m of FRONTEND_MENU_STRUCTURE) {
    if (normalizeBackendLabel(m.label) === collapsedNorm) return m.label
  }

  if (normalizeBackendLabel('Operations') === collapsedNorm || collapsedNorm.includes('operations')) {
    const hit = FRONTEND_MENU_STRUCTURE.find((x) => normalizeBackendLabel(x.label) === 'operations')
    return hit?.label || 'Operations'
  }

  const legacy = categorizePermissionFallback(code)
  const legacyNorm = normalizeBackendLabel(legacy)
  for (const m of FRONTEND_MENU_STRUCTURE) {
    if (normalizeBackendLabel(m.label) === legacyNorm) return m.label
  }
  if (legacyNorm === 'procurement' || legacyNorm === 'inventory') return 'Technical'
  if (legacyNorm === 'reports') return 'Operations'
  if (legacyNorm === 'dashboard') return 'Dashboard'
  return 'Other'
}

function pathScore(corpus: string, path: ProductionLayoutPath): number {
  let score = 0
  for (const term of Array.from(path.terms)) {
    if (term.length < 3) continue
    if (!corpus.includes(term)) continue
    score += Math.min(term.length, 24)
    if (/^\d+$/.test(term)) score -= 2
  }
  if (!path.moduleLabel) return 0
  const mn = normalizeBackendLabel(path.moduleLabel)
  if (mn && corpus.includes(mn)) score += 6

  /** Prefer deeper concrete leaves over synthetic parents */
  if (path.isSyntheticParent) score *= 0.85
  return score
}

function segmentsMatchNormalized(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false
  for (let i = 0; i < a.length; i++) {
    if (normalizeBackendLabel(a[i]) !== normalizeBackendLabel(b[i])) return false
  }
  return true
}

function lookupSortIndexForExplicit(moduleLabel: string, segments: string[]): number | null {
  const paths = [...getProductionLayoutPaths(), ...getAccountsLayoutPaths()]
  const hit = paths.find(
    (p) => p.moduleLabel === moduleLabel && segmentsMatchNormalized(p.segments, segments)
  )
  return hit != null ? hit.sortIndex : null
}

function fallbackExplicitSortIndex(moduleLabel: string, segments: string[]): number {
  const u = normalizeBackendLabel(moduleLabel)
  const ord = [...PRODUCTION_MODULE_ORDER]
  const mi = ord.findIndex((m) => normalizeBackendLabel(m) === u)
  const base = 950000 + (mi < 0 ? 999 : mi) * 2000
  const mix =
    segments.reduce((acc, s, i) => acc + (norm(s).charCodeAt(0) % 97) * (i + 3), 0) % 1800
  return base + mix
}

const SCORE_THRESHOLD = 5

function pickBestPathFromPool(corpus: string, pool: ProductionLayoutPath[]): {path: ProductionLayoutPath | null; score: number} {
  let best: ProductionLayoutPath | null = null
  let bestScore = -1
  for (const p of pool) {
    const s = pathScore(corpus, p)
    if (!best || s > bestScore || (s === bestScore && p.sortIndex < best.sortIndex)) {
      bestScore = s
      best = p
    }
  }
  return {path: best, score: bestScore}
}

/**
 * Prefer {@code permission_menu_map} labels from the catalog API when present; otherwise fall back to
 * production sidebar placement rules.
 */
export function resolveAuthoritativePermissionPlacement(
  entry: PermissionCatalogEntry,
  catalog?: PermissionCatalogEntry[]
): ProductionPermissionPlacement {
  const r = resolveCatalogEntry(entry)
  const menuFromMap = r.menuLabel?.trim()
  const subFromMap = r.subMenuLabel?.trim()
  if (
    isPermanentlyHiddenPermissionCode(r.permissionCode) ||
    isPermanentlyHiddenPlacement(r.moduleCategory, menuFromMap, subFromMap)
  ) {
    return assignProductionPermissionPlacement(entry, catalog)
  }
  if (menuFromMap) {
    const fallback = assignProductionPermissionPlacement(entry, catalog)
    const subMenuLabel = subFromMap && subFromMap.length > 0 ? subFromMap : '—'
    const pathSegments =
      subMenuLabel !== '—' ? [menuFromMap, subMenuLabel] : [menuFromMap]
    return {
      moduleLabel: fallback.moduleLabel,
      menuLabel: menuFromMap,
      subMenuLabel,
      pathSegments,
      sortIndex: fallback.sortIndex,
      unmapped: false,
      placementSource: 'explicit',
    }
  }
  return assignProductionPermissionPlacement(entry, catalog)
}

export function assignProductionPermissionPlacement(
  entry: PermissionCatalogEntry,
  catalog?: PermissionCatalogEntry[]
): ProductionPermissionPlacement {
  const r = resolveCatalogEntry(entry)
  const codeNorm = norm(r.permissionCode)

  const explicit = tryExplicitPlacement(codeNorm)
  if (explicit) {
    const segs = explicit.segments.filter((s) => String(s || '').trim().length > 0)
    const sortIndex =
      lookupSortIndexForExplicit(explicit.moduleLabel, segs) ?? fallbackExplicitSortIndex(explicit.moduleLabel, segs)
    if (segs.length <= 1) {
      const menu = segs[0] || (r.menuLabel?.trim()?.length ? r.menuLabel!.trim() : 'General')
      return {
        moduleLabel: explicit.moduleLabel,
        menuLabel: menu,
        subMenuLabel: '—',
        pathSegments: segs.length ? segs : [menu],
        sortIndex,
        unmapped: false,
        placementSource: 'explicit',
      }
    }
    return {
      moduleLabel: explicit.moduleLabel,
      menuLabel: segs[0],
      subMenuLabel: segs.slice(1).join(' › '),
      pathSegments: segs,
      sortIndex,
      unmapped: false,
      placementSource: 'explicit',
    }
  }

  const includeAccountsPaths =
    catalog != null ? catalogHasAnyAccountsPermission(catalog) : catalogEntryLooksAccounts(entry)
  const paths = [...getProductionLayoutPaths()]
  if (includeAccountsPaths) {
    paths.push(...getAccountsLayoutPaths())
  }

  const targetModule = guessProductionModuleLabel(entry)
  const corpus = entryCorpusNormalized(entry)

  const candidates = paths.filter((p) => p.moduleLabel === targetModule)
  const pool = candidates.length > 0 ? candidates : paths

  const leafFirst = pool.filter((p) => !p.isSyntheticParent)
  const leafPool = leafFirst.length > 0 ? leafFirst : pool
  const candLeaf = pickBestPathFromPool(corpus, leafPool)
  const candAll = pickBestPathFromPool(corpus, pool)

  let fuzzyBest: ProductionLayoutPath | null = null
  let fuzzyBestScore = -1
  for (const bundle of [candLeaf, candAll]) {
    const candidate = bundle.path
    const score = bundle.score
    if (!candidate) continue
    const preferLeaf =
      !fuzzyBest ||
      score > fuzzyBestScore ||
      (score === fuzzyBestScore &&
        !(candidate.isSyntheticParent ?? false) &&
        !!(fuzzyBest.isSyntheticParent ?? false))
    if (preferLeaf) {
      fuzzyBest = candidate
      fuzzyBestScore = score
    }
  }

  const fbMenu = (r.menuLabel?.trim() || '').length > 0 ? r.menuLabel!.trim() : 'General'
  const fbSub =
    r.subMenuLabel?.trim()?.length ?
      r.subMenuLabel!.trim()
      : categorizePermissionFallback(r.permissionCode)

  if (!fuzzyBest || fuzzyBestScore < SCORE_THRESHOLD) {
    const sub = collapsedOtherSubgroup(r.moduleCategory || '', r.permissionCode)
    if (process.env.NODE_ENV === 'development' && corpus.length > 0) {
      console.debug(
        `[productionPermissionGrouping] low-score placement (${fuzzyBestScore}) → Other · ${sub} ·`,
        r.permissionCode
      )
    }
    return {
      moduleLabel: 'Other',
      menuLabel: sub,
      subMenuLabel: fbMenu,
      pathSegments: [sub, fbMenu],
      sortIndex: 999999 + norm(r.permissionCode).charCodeAt(0),
      unmapped: true,
      otherSubgroup: sub,
      placementSource: 'unmapped',
    }
  }

  const segs = fuzzyBest.segments
  if (segs.length <= 1) {
    return {
      moduleLabel: fuzzyBest.moduleLabel,
      menuLabel: segs[0] || fbMenu,
      subMenuLabel: '—',
      pathSegments: segs,
      sortIndex: fuzzyBest.sortIndex,
      unmapped: false,
      placementSource: 'fuzzy',
    }
  }
  return {
    moduleLabel: fuzzyBest.moduleLabel,
    menuLabel: segs[0],
    subMenuLabel: segs.slice(1).join(' › '),
    pathSegments: segs,
    sortIndex: fuzzyBest.sortIndex,
    unmapped: false,
    placementSource: 'fuzzy',
  }
}

function collapsedOtherSubgroup(moduleCategory: string, code: string): string {
  const c =
    normalizeBackendLabel(moduleCategory).replace(/\s+/g, ' ') || normalizeBackendLabel(code).split(' ')[0] || 'Catalog'
  return c.slice(0, 80).replace(/\b\w/g, (l) => l.toUpperCase()) || 'Catalog'
}

export function productionModuleOrderIndex(label: string): number {
  const u = normalizeBackendLabel(label)
  const idx = [...PRODUCTION_MODULE_ORDER].findIndex((m) => normalizeBackendLabel(m) === u)
  return idx < 0 ? 999 : idx
}

export type ProductionUIModuleSection<T extends PermissionCatalogEntry> = {
  moduleLabel: string
  moduleSort: number
  sections: Array<{
    sectionKey: string
    /** Primary heading inside module (usually top sidebar label) */
    sectionTitle: string
    subtitle?: string
    sectionSort: number
    items: T[]
  }>
}

function sectionKey(pl: ProductionPermissionPlacement): string {
  if (pl.moduleLabel === 'Other' && pl.otherSubgroup) {
    return `Other::${normalizeBackendLabel(pl.otherSubgroup)}::${normalizeBackendLabel(pl.menuLabel)}`
  }
  return `${normalizeBackendLabel(pl.menuLabel)}::${normalizeBackendLabel(pl.subMenuLabel)}`
}

export type DedupeCatalogResult<T extends PermissionCatalogEntry> = {
  deduped: T[]
  /** Rows skipped because permission code duplicated an earlier catalog row */
  droppedDuplicateRows: number
  /** Permission codes appearing more than once in the incoming catalog */
  duplicateCatalogCodes: string[]
}

/**
 * Dedupes catalog by normalized permission code for display (preserves first row per code).
 */
export function dedupeCatalogByPermissionCode<T extends PermissionCatalogEntry>(catalog: T[]): DedupeCatalogResult<T> {
  const counts = new Map<string, number>()
  const deduped: T[] = []
  const seen = new Set<string>()
  let rowsWithCode = 0

  for (const e of catalog) {
    const c = norm(resolveCatalogEntry(e).permissionCode)
    if (!c) continue
    rowsWithCode++
    counts.set(c, (counts.get(c) ?? 0) + 1)
    if (seen.has(c)) continue
    seen.add(c)
    deduped.push(e)
  }

  const duplicateCatalogCodes = Array.from(counts.entries())
    .filter(([, n]) => n > 1)
    .map(([code]) => code)
    .sort()

  const droppedDuplicateRows = rowsWithCode - deduped.length

  if (process.env.NODE_ENV === 'development' && duplicateCatalogCodes.length > 0) {
    const preview = duplicateCatalogCodes.slice(0, 40).join(', ')
    const more =
      duplicateCatalogCodes.length > 40 ? ` …(+${duplicateCatalogCodes.length - 40} more codes)` : ''
    console.warn(
      `[productionPermissionGrouping] catalog has duplicate permission codes (${duplicateCatalogCodes.length} codes, ${droppedDuplicateRows} extra rows). Preview: ${preview}${more}`
    )
  }

  return {deduped, droppedDuplicateRows, duplicateCatalogCodes}
}

export type ProductionGroupingValidation = {
  totalCatalogCodes: number
  uniqueCatalogCodes: number
  duplicateCatalogCodes: string[]
  droppedDuplicateCatalogRows: number
  groupedModuleCount: number
  groupedSectionCount: number
  groupedFlatItemCount: number
  /** Sorted unique codes present in flattened grouped model */
  renderedCodes: string[]
  uniqueRenderedCodes: number
  missingCodes: string[]
  duplicateRenderedCodes: string[]
  unmappedCodes: string[]
  lowConfidenceCodes: string[]
}

/**
 * Asserts that grouped UI output contains each **deduped** catalog code exactly once.
 * Pass the **raw** catalog to surface API-side duplicate codes vs UI merge bugs.
 */
export function validateProductionPermissionGrouping<T extends PermissionCatalogEntry>(
  catalogRaw: T[],
  grouped: ProductionUIModuleSection<T>[]
): ProductionGroupingValidation {
  const {deduped, duplicateCatalogCodes, droppedDuplicateRows} = dedupeCatalogByPermissionCode(catalogRaw)

  const flatItems = grouped.flatMap((m) => m.sections.flatMap((s) => s.items))
  const groupedSectionCount = grouped.reduce((n, m) => n + m.sections.length, 0)

  const rendered = flatItems.map((e) => norm(resolveCatalogEntry(e).permissionCode)).filter(Boolean)
  const uniqRenderedSet = new Set(rendered)

  const countMap = new Map<string, number>()
  for (const c of rendered) countMap.set(c, (countMap.get(c) ?? 0) + 1)
  const duplicateRenderedCodes = Array.from(countMap.entries())
    .filter(([, n]) => n > 1)
    .map(([c]) => c)
    .sort()

  const uniqueCatalogCodesList = deduped.map((e) => norm(resolveCatalogEntry(e).permissionCode)).filter(Boolean)
  const uniqueCatalogSet = new Set(uniqueCatalogCodesList)

  const missingCodes = Array.from(uniqueCatalogSet.values()).filter((c) => !uniqRenderedSet.has(c)).sort()

  const unmappedSet = new Set<string>()
  const lowConfidenceSet = new Set<string>()
  for (const e of deduped) {
    const c = norm(resolveCatalogEntry(e).permissionCode)
    if (!c) continue
    const pl = assignProductionPermissionPlacement(e, deduped)
    if (pl.unmapped) unmappedSet.add(c)
    else if (pl.placementSource === 'fuzzy') lowConfidenceSet.add(c)
  }

  const totalCatalogCodes = catalogRaw.filter((row) => norm(resolveCatalogEntry(row).permissionCode)).length

  return {
    totalCatalogCodes,
    uniqueCatalogCodes: uniqueCatalogSet.size,
    duplicateCatalogCodes,
    droppedDuplicateCatalogRows: droppedDuplicateRows,
    groupedModuleCount: grouped.length,
    groupedSectionCount,
    groupedFlatItemCount: flatItems.length,
    renderedCodes: Array.from(uniqRenderedSet.values()).sort(),
    uniqueRenderedCodes: uniqRenderedSet.size,
    missingCodes,
    duplicateRenderedCodes,
    unmappedCodes: Array.from(unmappedSet.values()).sort(),
    lowConfidenceCodes: Array.from(lowConfidenceSet.values()).sort(),
  }
}

/**
 * Builds nested groups (production module → menu/sub-menu sections) after search/filter.
 */
export function buildProductionUIModules<T extends PermissionCatalogEntry>(
  catalog: T[],
  opts: {
    searchQuery: string
    filter?: (e: T) => boolean
    mapRow?: (e: T) => T
    extraSearch?: (e: T, queryUpper: string) => boolean
  }
): ProductionUIModuleSection<T>[] {
  const q = opts.searchQuery.trim().toUpperCase()
  const dup = dedupeCatalogByPermissionCode(catalog as PermissionCatalogEntry[])
  let list = dup.deduped.map((e) => (opts.mapRow ? opts.mapRow(e as T) : (e as T)))
  const hasAccounts = catalogHasAnyAccountsPermission(list as PermissionCatalogEntry[])

  const withPlacement = list
    .filter((e) => !isPermanentlyHiddenPermissionCode(resolveCatalogEntry(e).permissionCode))
    .map((e) => ({
      raw: e,
      pl: assignProductionPermissionPlacement(e as PermissionCatalogEntry, list as PermissionCatalogEntry[]),
    }))
    .filter(({pl}) => !isPermanentlyHiddenPlacement(pl.moduleLabel, pl.menuLabel, pl.subMenuLabel))

  const filtered =
    opts.filter ?
      withPlacement.filter(({raw}) => opts.filter!(raw))
    : withPlacement

  const searchFiltered =
    !q ?
      filtered
    : filtered.filter(({raw}) => {
        const r = resolveCatalogEntry(raw)
        const base =
          r.permissionCode.toUpperCase().includes(q) ||
          (r.displayName || '').toUpperCase().includes(q) ||
          (r.description || '').toUpperCase().includes(q)
        const pl = assignProductionPermissionPlacement(raw as PermissionCatalogEntry, list as PermissionCatalogEntry[])
        const plTxt = `${pl.moduleLabel} ${pl.menuLabel} ${pl.subMenuLabel}`.toUpperCase()
        if (base || plTxt.includes(q)) return true
        return opts.extraSearch?.(raw, q) ?? false
      })

  const moduleMap = new Map<string, Map<string, {pl: ProductionPermissionPlacement; items: T[]}>>()

  for (const {raw, pl} of searchFiltered) {
    if (pl.moduleLabel === 'Accounts' && !hasAccounts) continue
    if (!moduleMap.has(pl.moduleLabel)) moduleMap.set(pl.moduleLabel, new Map())
    const sk = sectionKey(pl)
    const modSec = moduleMap.get(pl.moduleLabel)!
    if (!modSec.has(sk)) {
      modSec.set(sk, {pl, items: []})
    }
    modSec.get(sk)!.items.push(raw)
  }

  const modules: ProductionUIModuleSection<T>[] = []
  for (const [moduleLabel, secMap] of Array.from(moduleMap.entries())) {
    const sections = Array.from(secMap.values())
      .map(({pl, items}) => ({
        sectionKey: sectionKey(pl),
        sectionTitle: pl.menuLabel,
        subtitle: pl.subMenuLabel && pl.subMenuLabel !== '—' ? pl.subMenuLabel : undefined,
        sectionSort: pl.sortIndex,
        items: sortEntriesInModule(items as {permissionCode: string; displayName?: string | null}[]) as T[],
      }))
      .sort((a, b) => {
        if (a.sectionSort !== b.sectionSort) return a.sectionSort - b.sectionSort
        return a.sectionTitle.localeCompare(b.sectionTitle)
      })
    modules.push({
      moduleLabel,
      moduleSort: productionModuleOrderIndex(moduleLabel),
      sections,
    })
  }

  modules.sort((a, b) => {
    if (a.moduleSort !== b.moduleSort) return a.moduleSort - b.moduleSort
    return a.moduleLabel.localeCompare(b.moduleLabel)
  })

  return modules
}

/** Flattened optgroup / legacy shape: one group per "Module · Menu" */
export function groupPermissionCatalogForProductionUI<T extends PermissionCatalogEntry>(
  catalog: T[],
  options: {
    searchQuery: string
    filter?: (e: T) => boolean
    mapRow?: (e: T) => T
    extraSearch?: (e: T, queryUpper: string) => boolean
  }
): {moduleKey: string; items: T[]}[] {
  const tree = buildProductionUIModules(catalog, options)
  const out: {moduleKey: string; moduleOrder: number; sectionOrder: number; items: T[]}[] = []
  for (const mod of tree) {
    for (const sec of mod.sections) {
      const label =
        sec.subtitle && sec.subtitle !== '—' ?
          `${mod.moduleLabel} · ${sec.sectionTitle} · ${sec.subtitle}`
        : `${mod.moduleLabel} · ${sec.sectionTitle}`
      out.push({
        moduleKey: label,
        moduleOrder: mod.moduleSort,
        sectionOrder: sec.sectionSort,
        items: sec.items,
      })
    }
  }
  out.sort((a, b) => {
    if (a.moduleOrder !== b.moduleOrder) return a.moduleOrder - b.moduleOrder
    if (a.sectionOrder !== b.sectionOrder) return a.sectionOrder - b.sectionOrder
    return a.moduleKey.localeCompare(b.moduleKey)
  })
  return out.map(({moduleKey, items}) => ({moduleKey, items}))
}

export function countUnmappedProductionPlacements(catalog: PermissionCatalogEntry[]): number {
  return catalog.filter((e) => assignProductionPermissionPlacement(e, catalog).unmapped).length
}

const DEBUG_ACTION_BY_SUFFIX: Record<string, string> = {
  READ: 'View',
  VIEW: 'View',
  SHOW: 'View',
  LIST: 'View',
  FETCH: 'View',
  CREATE: 'Add',
  INSERT: 'Add',
  NEW: 'Add',
  UPDATE: 'Edit',
  MODIFY: 'Edit',
  EDIT: 'Edit',
  DELETE: 'Delete',
  REMOVE: 'Delete',
  APPROVE: 'Workflow',
  APPROVAL: 'Workflow',
  DOWNLOAD: 'Download',
  EXPORT: 'Export',
  IMPORT: 'Import',
  MANAGE: 'Manage',
  ASSIGN: 'Assign',
  SEND: 'Send',
  AWARD: 'Award',
  CLOSE: 'Close',
  SUBMIT: 'Submit',
  COMPARE: 'Compare',
  SELECT: 'Select',
  REWORK: 'Rework',
}

const DEBUG_ACTION_BY_PREFIX: Record<string, string> = {
  READ: 'View',
  VIEW: 'View',
  SHOW: 'View',
  CREATE: 'Add',
  ADD: 'Add',
  UPDATE: 'Edit',
  EXPORT: 'Export',
  IMPORT: 'Import',
}

function debugShortDisplayName(displayName?: string | null): string | undefined {
  const t = (displayName || '').trim()
  if (!t) return undefined
  return t.replace(/^\s*can\s+/i, '').trim() || t
}

/** Matrix-style action label for diagnostics (≈ advanced matrix bucketing, not identical). */
export function approximateMatrixActionLabel(permissionCode: string, displayName?: string | null): string {
  const u = norm(permissionCode)
  if (!u) return humanizePermissionCode(permissionCode)
  const parts = u.split('_').filter(Boolean)
  if (parts.length === 0) return humanizePermissionCode(permissionCode)

  const last = parts[parts.length - 1]
  const suf = DEBUG_ACTION_BY_SUFFIX[last]
  if (suf && parts.length >= 2) return suf

  const first = parts[0]
  const pre = DEBUG_ACTION_BY_PREFIX[first]
  if (pre && parts.length >= 2) return pre

  const fromName = debugShortDisplayName(displayName)
  if (fromName) return fromName.slice(0, 120)

  return humanizePermissionCode(permissionCode)
}

export type ProductionGroupingFlatDebugRow = {
  module: string
  menu: string
  submenu: string
  actionLabel: string
  permissionCode: string
  placementSource: 'explicit' | 'fuzzy' | 'unmapped'
  displayName?: string | null
}

/** One flat row per **deduped** catalog permission (sorted for stable consoles / JSON export). */
export function buildProductionGroupingFlatDebugRows(
  catalogRaw: PermissionCatalogEntry[]
): ProductionGroupingFlatDebugRow[] {
  const {deduped} = dedupeCatalogByPermissionCode(catalogRaw as PermissionCatalogEntry[])
  const out: ProductionGroupingFlatDebugRow[] = []
  for (const raw of deduped) {
    const e = resolveCatalogEntry(raw)
    const pl = assignProductionPermissionPlacement(raw as PermissionCatalogEntry, deduped)
    const code = e.permissionCode
    const normCode = norm(code)
    if (!normCode) continue
    out.push({
      module: pl.moduleLabel,
      menu: pl.menuLabel,
      submenu: pl.subMenuLabel,
      actionLabel: approximateMatrixActionLabel(code, e.displayName),
      permissionCode: code,
      placementSource: pl.placementSource,
      displayName: e.displayName ?? null,
    })
  }
  out.sort((a, b) => {
    const c =
      `${a.module}::${a.menu}::${a.submenu}::${a.actionLabel}`.localeCompare(
        `${b.module}::${b.menu}::${b.submenu}::${b.actionLabel}`
      )
    if (c !== 0) return c
    return normalizeBackendLabel(a.permissionCode).localeCompare(normalizeBackendLabel(b.permissionCode))
  })
  return out
}

export type PermissionGroupingDebugSnapshot = {
  /** Unique catalog codes after dedupe (stable order — sorted) */
  catalogCodes: string[]
  groupedRows: ProductionGroupingFlatDebugRow[]
  validation: ProductionGroupingValidation
}

export function buildPermissionGroupingDebugSnapshot(
  catalogRaw: PermissionCatalogEntry[]
): PermissionGroupingDebugSnapshot {
  const groupedModel = buildProductionUIModules(catalogRaw, {searchQuery: ''})
  const validation = validateProductionPermissionGrouping(catalogRaw, groupedModel)
  const groupedRows = buildProductionGroupingFlatDebugRows(catalogRaw)
  const {deduped} = dedupeCatalogByPermissionCode(catalogRaw as PermissionCatalogEntry[])
  const catalogCodes = deduped
    .map((e) => norm(resolveCatalogEntry(e).permissionCode))
    .filter(Boolean)
    .sort()
  return {catalogCodes, groupedRows, validation}
}
